/** @format */

import React, { useEffect, useState, FunctionComponent } from 'react'
import TablePage from 'src/components/TablePage'
import { _$, Tooltip, Icon, Message, Button, Tag, Switch, Editable } from 'kpc-react'
import useFormTable from 'src/hooks/useFormTable'
import { useHistory } from 'react-router'
import { API, EditEnablePolicy, DeleteAlarmPolicy } from 'src/api/ApiAlarmServer'
import { MonitorSearch, Divider } from 'src/components'
import styled from 'styled-components'
import variable from 'src/static/styles/var'
import useTableGroup from 'src/hooks/useTableGroup'
import { handleDataUnitsValue } from 'src/containers/MonitorCreate/AlarmRule/constants'
import { methodObject, compareObject } from 'src/config'
import { request } from 'src/utils'
import TacticsInstancesDialog from './TacticsInstancesDialog'

interface OwnProps {
  className?: string
}

const TacticsList: FunctionComponent<OwnProps> = ({ className }) => {
  const history = useHistory()
  const [tableGroup, setTableGroup] = useState({
    productName: 999
  })
  const productList = useTableGroup('subProductList')
  const [policyList, setPolicyList] = useState([])
  const [policyName, setPolicyName] = useState<string>('')
  // 已选实例dialog
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  const [dialogPolicId, setDialogPolicId] = useState<number>()
  const [dialogProjectName, setDialogProjectName] = useState<string>('')

  // 告警策略名称验证
  const policyNameValidate = value => {
    return /^[a-z,\u4E00-\u9FA5,0-9,A-Z,-_@#.]{2,64}$/.test(value)
  }

  // 修改告警策略名称
  const changePolictName = async (name, id) => {
    const result = await request(API.ModifyPolicyName, {
      method: 'PUT',
      data: {
        policyId: id,
        policyName: name
      }
    })
    if (result?.code === 'Success') {
      Message.success(_$('修改成功'))
      run()
    }
  }

  // 设置/取消默认告警策略
  const handleDefaultPolicy = async (id, type) => {
    const result = await request(type === 1 ? API.CancelPolicyDefault : API.SetPolicyAsDefault, {
      method: 'POST',
      data: {
        policyId: id
      }
    })
    if (result?.code === 'Success') {
      Message.success(_$('修改成功'))
      run()
    }
  }

  const schemeThreshold = {
    policyName: {
      // width: 150,
      title: _$('策略名称'),
      template: function (item) {
        return (
          <div className="policyType_cell">
            <Editable
              value={item.policyName}
              validate={value => policyNameValidate(value)}
              onChange={(_c, value, old) => {
                changePolictName(value, item.policyId)
              }}
            >
              <p className="metric-legend">{item.policyName}</p>
            </Editable>
          </div>
        )
      }
    },
    productName: {
      title: _$('产品类型'),
      width: 150,
      group: productList,
      template: function (item) {
        return (
          <>
            {item.productName}
            {item.policyType === 1 ? (
              <Tag type="primary" size="mini">
                {_$('默认')}
              </Tag>
            ) : null}
          </>
        )
      }
    },
    triggerRules: {
      title: _$('告警规则'),
      template: function (item) {
        return (
          <ul className="rule-list">
            {item.triggerRules.map(
              ({
                itemKey,
                itemName,
                triggerValue,
                units,
                period,
                method,
                compare,
                effectBT,
                effectET,
                ruleId
              }) => {
                if (itemKey === 'agent.ping' || itemKey === 'kdc.alive') {
                  return itemName
                }
                const { units: unit, triggerValue: value } = handleDataUnitsValue(
                  triggerValue,
                  units
                )
                const text = `${itemName} ${parseInt(period)}${_$('分钟')} ${
                  methodObject[method]
                } ${compareObject[compare] || compare} ${value}${unit} ${_$(
                  '告警生效时间'
                )}${effectBT}-${effectET}`
                return (
                  <li key={ruleId}>
                    <Tooltip key={ruleId} content={_$(text)}>
                      <span>{_$(text)}</span>
                    </Tooltip>
                  </li>
                )
              }
            )}
          </ul>
        )
      }
    },
    instanceIds: {
      title: _$('关联实例'),
      width: 150,
      template: function (item) {
        return (
          <>
            {item?.resourceBindType === 2 ? (
              <div
                className="cursor-pointer"
                onClick={() => openDialog(item?.policyId, item?.projectName || '')}
              >
                <p>
                  <a>
                    {_$('项目')}：{item.projectName || ''}
                  </a>
                </p>
                <p>
                  <a>
                    {_$('实例')}：
                    {item.instanceIds ? item.instanceIds.length : item.instanceInfoList.length}
                  </a>
                </p>
              </div>
            ) : null}
            {item?.resourceBindType === 127 ? (
              <p>
                {_$('项目')}：{item.projectName || ''}
                <span className="deleted_color">({_$('已删除')})</span>
              </p>
            ) : null}
            {item?.resourceBindType !== 2 && item?.resourceBindType !== 127 ? (
              <a onClick={() => openDialog(item?.policyId, '')}>
                {_$('实例')}：
                {item.instanceIds ? item.instanceIds.length : item.instanceInfoList.length}
              </a>
            ) : null}
          </>
        )
      }
    },
    contactInfoList: {
      width: 100,
      title: (
        <span>
          {_$('告警接收人')}
          <Tooltip content={_$('未配置告警接收人将不会收到告警')}>
            <Icon
              style={{ fontSize: '14px', marginLeft: '4px' }}
              className="icon ion-help-circled"
            />
          </Tooltip>
        </span>
      ),
      template: function (item) {
        const contactList = item.userNotice
        const group = contactList && [].concat(contactList).filter(i => i?.contactFlag === 1)
        const user = contactList && [].concat(contactList).filter(i => i?.contactFlag === 2)
        return (
          <span>
            {!contactList || !contactList?.length ? (
              <span
                className="none-setting"
                onClick={event => history.push(`/monitor/alarmServer/rule/detail/${item.policyId}`)}
              >
                {_$('未配置')}
              </span>
            ) : (
              <>
                <span>
                  {_$('联系组')}：{group?.length}
                </span>
                <br />
                <span>
                  {_$('联系人')}：{user?.length}
                </span>
              </>
            )}
          </span>
        )
      }
    },
    enable: {
      title: _$('状态'),
      width: 150,
      template: function (item) {
        return (
          <Switch
            disabled={item.policyType === 1}
            on={_$('启用')}
            off={_$('禁用')}
            width="62"
            value={item.enabled === 0}
            onClick={async () => {
              if (item.policyType !== 1) {
                const result = await EditEnablePolicy(item.enabled, {
                  PolicyIds: [item.policyId]
                })
                if (result?.code === 'Success') {
                  Message.success(item.enabled === 0 ? _$('禁用成功') : _$('启用成功'))
                  run()
                }
              }
            }}
          />
        )
      }
    },
    action: {
      title: _$('操作'),
      width: 150,
      template: function (item) {
        return (
          <div className="ksc-space">
            {/* 告警历史 */}
            <a
              onClick={event => {
                const policyId = encodeURIComponent(item.policyId)
                const policyName = encodeURIComponent(item.policyName)
                const src = `/monitor/alarmServer/history/detail/${policyId}/${policyName}`
                history.push(src)
              }}
            >
              {_$('告警历史')}
            </a>
            <Divider />
            {/* 修改 */}
            <a onClick={event => history.push(`/monitor/alarmServer/rule/detail/${item.policyId}`)}>
              {_$('修改')}
            </a>
            {item.policyType !== 1 ? (
              <>
                <Divider />
                <Tooltip
                  content={_$('确认删除当前策略？')}
                  confirm
                  theme="light"
                  trigger="click"
                  onOk={async () => {
                    const result = await DeleteAlarmPolicy({ PolicyIds: [item.policyId] })
                    if (result?.code === 'Success') {
                      Message.success(_$('删除成功'))
                      run()
                    }
                  }}
                >
                  <a>{_$('删除')}</a>
                </Tooltip>
              </>
            ) : null}
            {item.policyType === 1 && item.resourceBindType !== 2 ? (
              <>
                <Divider />
                <Tooltip
                  content={_$('确认取消当前默认策略？')}
                  confirm
                  theme="light"
                  trigger="click"
                  onOk={() => {
                    handleDefaultPolicy(item.policyId, item.policyType)
                  }}
                >
                  <a>{_$('取消默认策略')}</a>
                </Tooltip>
              </>
            ) : null}
            {item.policyType !== 1 && item.enabled === 0 && item.resourceBindType !== 2 ? (
              <>
                <Divider />
                <Tooltip
                  content={_$('确认设置当前默认策略')}
                  confirm
                  theme="light"
                  trigger="click"
                  onOk={() => {
                    handleDefaultPolicy(item.policyId, item.policyType)
                  }}
                >
                  <a>{_$('设置默认策略')}</a>
                </Tooltip>
              </>
            ) : null}
          </div>
        )
      }
    }
  }
  const { run, result, total, pageState, loading }: any = useFormTable(
    API.GetPolicyList,
    {
      params: {
        ProductType: tableGroup.productName,
        PolicyName: policyName
      }
    },
    data => {
      return {
        tableData: data?.alarmPolicyList,
        totalCount: data?.totalCount
      }
    }
  )

  useEffect(() => {
    if (Array.isArray(result)) {
      const list = result
      if (Array.isArray(list)) {
        setPolicyList(list)
      }
    }
  }, [result])

  // 打开已选实例Dialog
  const openDialog = (polictId, projectName) => {
    setDialogPolicId(polictId)
    setDialogProjectName(projectName)
    setDialogVisible(true)
  }

  // 关闭已选实例Dialog
  const closeDialog = value => {
    // if (!value) {
    //   setDialogPolicId(null)
    //   setDialogProjectName('')
    // }
    setDialogVisible(value)
  }

  return (
    <div className={className}>
      <div className="header">
        <div>
          <Button
            type="primary"
            onClick={event => {
              history.push(`/monitor/alarmServer/rule/create`)
            }}
          >
            <Icon className="ion-plus-round"></Icon>
            {_$('新建告警策略')}
          </Button>
          <span className="text">
            <span dangerouslySetInnerHTML={{ __html: _$('告警服务升级了，查看如何') }} />
          </span>
        </div>
        <div>
          <MonitorSearch
            value={policyName}
            placeholder={_$('输入告警策略名称')}
            onChange={value => {
              setPolicyName(value)
              run({
                PageIndex: 1,
                ProductType: tableGroup.productName,
                PolicyName: value
              })
            }}
          />
        </div>
      </div>

      <TablePage
        tableProps={{
          scheme: schemeThreshold,
          data: policyList,
          checkType: 'none',
          resizable: true,
          group: tableGroup,
          'on$change-group': (_c, value) => {
            setTableGroup(value)
            run({
              PageIndex: 1,
              ProductType: value.productName,
              PolicyName: policyName
            })
          }
        }}
        run={run}
        loading={loading}
        pageProps={{ total, ...pageState }}
      />

      <TacticsInstancesDialog
        visible={dialogVisible}
        policyId={dialogPolicId}
        projectName={dialogProjectName}
        onClose={closeDialog}
      />
    </div>
  )
}

export default styled(TacticsList)`
  .header {
    min-height: 32px;
    line-height: 32px;
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    span.text {
      margin-left: 8px;
    }
  }
  .policyType_cell {
    display: flex;
    align-items: center;
    .k-input {
      width: 100%;
    }
    .k-editable {
      .c-ellipsis {
        white-space: normal;
      }
    }
    .metric-legend {
      max-width: 100%;
      margin: 0;
      word-break: break-all;
    }
  }
  .none-setting {
    cursor: pointer;
    color: ${variable.errorColor};
  }
  .k-table {
    tr {
      .k-tag {
        margin-left: 5px;
      }
      .text-overflow,
      p {
        margin: 0;
      }
      .ksc-space {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }
      .k-icon-edit {
        font-size: 14px;
        margin-left: 5px;
        opacity: 0;
        transition: opacity 0.15s ease-out;
      }
      &:hover {
        .k-icon-edit {
          opacity: 1;
        }
      }
    }
    .rule-list {
      li {
        max-width: 100%;
        height: 18px;
        span {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
`
